import { useState } from 'react'
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog'
import { useCreditsDialog } from './CreditsDialogContext'
import { TopUpSubtitle } from './TopUpSubtitle'
import { CreditsOverview } from './CreditsOverview'
import { PackagesGrid } from './PackagesGrid'
import { RegionLine } from './RegionLine'
import { MethodPicker } from './MethodPicker'
import { WaitingPayment } from './WaitingPayment'
import { openPaymentWindow } from './lib/openPaymentWindow'
import { useCardPaymentAccess } from './useCardPaymentAccess'
import { useMe } from '@/features/auth/useMe'
import type { CreditPackage } from './lib/paymentMethods'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Четыре шага в одной оболочке: окно кредитов → витрина → способ оплаты →
 * ожидание. Окно кредитов — вход «просто посмотреть»; открытие из отказа
 * прогона его минует, потому что там автору нужны деньги, а не чтение.
 *
 * Экран ожидания показывается по ШАГУ (`step` провайдера), а не по одному лишь
 * наличию транзакции: наблюдение за платежом обязано пережить и возврат к
 * витрине, и закрытие диалога, иначе брошенная оплата запирает диалог на всю
 * сессию.
 */
export const TopUpDialog = () => {
  const {
    isOpen,
    deficit,
    close,
    payment,
    step,
    setPayment,
    showPackages,
    abandonPayment,
    isReturnTab,
  } = useCreditsDialog()
  const m = useMessages(messages)
  const [picked, setPicked] = useState<CreditPackage | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Профиль читается ОДИН раз на весь диалог и раздаётся пропом: витрина и шаг
  // оплаты обязаны отвечать на «можно ли картой» одинаково, а два независимых
  // вызова хука разошлись бы на времени доезда ответа. Витрине хватает флага,
  // шагу оплаты нужно ещё и различать «почты нет» от «профиль не прочитан» —
  // он единственный называет причину словами.
  const cardAccess = useCardPaymentAccess()
  const canPayByCard = cardAccess === 'allowed'
  // Страна общая с веб-академией, выбор — в настройках аккаунта (строка
  // региона ведёт туда); не прочитана — `null`.
  const country = useMe().data?.country ?? null

  const backToPackages = (text?: string) => {
    setPicked(null)
    // Транзакцию НЕ снимаем: за живым платежом всё ещё стоит вкладка воронки,
    // и успех должен доехать до автора тостом даже отсюда.
    showPackages()
    setNotice(text ?? null)
  }

  /** Выход с экрана ожидания: транзакцию бросаем, автор выбирает заново. */
  const leavePayment = () => {
    setPicked(null)
    setNotice(null)
    abandonPayment()
  }

  const pick = (pkg: CreditPackage) => {
    setNotice(null)
    setPicked(pkg)
  }

  /**
   * Закрытие диалога. Оно же — выход с терминального экрана.
   *
   * Судьбу транзакции решает не закрытие, а СЛЕДУЮЩЕЕ открытие (провайдер):
   * живую покупку оно возвращает на её шаг, отработавшую снимает и показывает
   * витрину. Здесь чистится только местное: выбранный пакет забываем — за ним,
   * в отличие от транзакции, не стоит никакого процесса, а следующее открытие
   * может прийти из ДРУГОГО отказа, с другим дефицитом и другой подходящей
   * меткой на витрине.
   */
  const finish = () => {
    setPicked(null)
    setNotice(null)
    close()
  }

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) finish()
      }}
      className={
        payment !== null && step === 'payment'
          ? 'max-w-md'
          : step === 'credits'
            ? 'max-w-3xl'
            : 'max-w-2xl'
      }
      title={step === 'credits' ? m.creditsTitle : m.buyCreditsTitle}
      // В окне кредитов баланс уже стоит крупно — подзаголовок повторял бы его.
      // Пустым описание оставлять нельзя: на него ссылается `aria-describedby`.
      description={
        step === 'credits' ? (
          <span className="sr-only">{m.creditsDescription}</span>
        ) : (
          <TopUpSubtitle deficit={deficit} />
        )
      }
    >
      {payment !== null && step === 'payment' ? (
        <WaitingPayment
          onBack={leavePayment}
          onDone={() => {
            finish()
            // Вкладка пришла из воронки: работа автора — в исходной, и там уже
            // стоит та же галочка. Не дал браузер закрыть — автор остаётся на
            // «Использовании» с новым балансом.
            if (isReturnTab) window.close()
          }}
        />
      ) : step === 'credits' ? (
        <CreditsOverview onTopUp={showPackages} />
      ) : picked ? (
        <MethodPicker
          pkg={picked}
          cardAccess={cardAccess}
          country={country}
          onBack={backToPackages}
          onPaid={(next) => {
            setPayment(next)
            // Пустой `paymentUrl` (случай `processing: true`) вкладки не
            // требует, ждём статус сразу.
            if (next.paymentUrl) openPaymentWindow(next.paymentUrl)
          }}
        />
      ) : (
        // pt-4: метка «Хватит на этот запуск» торчит над карточкой и иначе липнет к подзаголовку.
        <div className="space-y-3 pt-4">
          {notice ? <p className="text-sm text-destructive">{notice}</p> : null}
          <RegionLine country={country} onNavigate={finish} />
          <PackagesGrid
            deficit={deficit}
            canPayByCard={canPayByCard}
            country={country}
            onSelect={pick}
          />
        </div>
      )}
    </ResponsiveDialog>
  )
}
